import React, { useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { db } from '../../services/firebase';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import * as Papa from 'papaparse';
import ComplianceChecker from './ComplianceChecker';
import './ContributorOnboard.css';

const ContributorOnboard = () => {
  const { user } = useContext(UserContext);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Application Form State
  const [applicationData, setApplicationData] = useState({
    businessName: '',
    businessType: 'independent_creator',
    website: '',
    portfolio: '',
    bio: '',
    estimatedProducts: '',
    socialMedia: {
      instagram: '',
      website: '',
      other: ''
    }
  });

  // CSV Upload State
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvPreview, setCsvPreview] = useState([]);
  const [platformType, setPlatformType] = useState('shopify');

  // Legal acceptance state
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [showLegalTerms, setShowLegalTerms] = useState(false);

  // Contributor terms
  const CONTRIBUTOR_TERMS = {
    title: "Artifex Contributor Agreement",
    sections: {
      imageRights: {
        title: "Image Rights and Content Ownership",
        terms: [
          "Contributor warrants they own or have legal rights to all images, descriptions, and content submitted to Artifex.",
          "Contributor grants Artifex a non-exclusive, worldwide license to use, display, modify, and promote submitted content across all Artifex channels including website, social media, editorial, and Apple News.",
          "Contributor agrees to indemnify and hold harmless Artifex against any claims, damages, or legal costs related to submitted content.",
          "Artifex reserves the right to immediately remove any content if rights are challenged, disputed, or if Artifex reasonably believes content may infringe third-party rights."
        ]
      },
      qualityStandards: {
        title: "Product and Content Standards", 
        terms: [
          "All products and content must meet Artifex's curation and aesthetic standards as determined by Artifex in its sole discretion.",
          "Contributor agrees to maintain accurate product information, pricing, and availability. False or misleading information may result in immediate account suspension.",
          "Artifex reserves the right to reject, remove, or modify any products or content that don't meet platform standards without prior notice.",
          "Contributor remains solely responsible for all product fulfillment, shipping, customer service, returns, and warranty claims.",
          "Contributor may not list items that violate any laws, infringe intellectual property, or contain prohibited content as defined by Artifex policies."
        ]
      },
      revenue: {
        title: "Revenue Sharing and Commission",
        terms: [
          "Standard commission rate: 12% of gross sales price for all transactions originated through Artifex platform.",
          "Founding Contributors (first 50 approved): Permanent 10% commission rate regardless of future rate changes.",
          "Premium Contributors (>$2,000 monthly sales for 3+ consecutive months): 10% commission rate.",
          "Commission rates may be adjusted for new contributors with 60 days written notice. Existing contributor rates remain grandfathered.",
          "Contributor agrees to provide accurate sales reporting for attribution tracking. Artifex may audit sales data with 30 days notice.",
          "Contributor is solely responsible for all taxes, fees, and legal compliance related to their earnings.",
          "Commission payments processed monthly within 30 days of month-end, subject to 14-day hold for new contributors."
        ]
      },
      compliance: {
        title: "Platform Compliance and Monitoring",
        terms: [
          "Contributor will not upload content that violates third-party intellectual property rights, privacy rights, or any applicable laws.",
          "Contributor will not engage in deceptive pricing, false advertising, review manipulation, or any fraudulent practices.",
          "Contributor expressly agrees to Artifex's right to monitor, scan, and audit all submissions using automated and manual review processes.",
          "Artifex may suspend contributor privileges immediately for violations. Contributors may appeal suspensions within 14 days with supporting documentation.",
          "Repeated violations or serious misconduct may result in permanent account termination and forfeiture of pending commissions."
        ]
      },
      termination: {
        title: "Termination and Data Rights",
        terms: [
          "Either party may terminate this agreement with 30 days written notice for any reason.",
          "Artifex may terminate immediately for material breach, illegal activity, or violation of platform standards.",
          "Upon termination, Artifex will honor existing affiliate commissions for sales made within 90 days, minus any applicable fees or chargebacks.",
          "Contributor may request content removal within 30 days of termination. After 90 days, Artifex may permanently delete contributor data.",
          "Contributor acknowledges that termination may result in loss of sales, exposure, and business opportunities, for which Artifex bears no liability.",
          "Surviving provisions: Revenue sharing for pre-termination sales, indemnification, intellectual property licenses, and dispute resolution."
        ]
      },
      legal: {
        title: "General Legal Terms",
        terms: [
          "This agreement constitutes the entire agreement between parties and supersedes all prior negotiations or agreements.",
          "Agreement governed by California law. Any disputes resolved through binding arbitration in Los Angeles County, California.",
          "If any provision is deemed unenforceable, remaining provisions remain in full effect.",
          "Contributor may not assign rights under this agreement. Artifex may assign rights to affiliates or in connection with business transfers.",
          "No modification of terms except in writing signed by both parties. Continued use after posted changes constitutes acceptance of new terms.",
          "Contributor acknowledges reading and understanding all terms and enters this agreement voluntarily with opportunity to seek independent legal counsel."
        ]
      }
    }
  };

  // CSV Template Downloads with enhanced instructions
  const csvTemplates = {
    shopify: {
      name: 'Shopify Export Template',
      headers: ['Title', 'Description', 'Price', 'Image URL', 'Product URL', 'Category', 'Tags'],
      example: [
        'Handmade Ceramic Vase', 
        'Beautiful minimal ceramic vase perfect for modern interiors', 
        '89.00', 
        'https://yourstore.com/images/vase.jpg', 
        'https://yourstore.com/products/ceramic-vase',
        'Home Decor',
        'ceramic, handmade, vase, minimal'
      ],
      instructions: [
        "1. Go to your Shopify Admin → Products",
        "2. Click 'Export' button at the top",
        "3. Select 'Current page' or 'All products'", 
        "4. Choose 'CSV for Excel, Numbers, or other spreadsheet programs'",
        "5. Click 'Export products'",
        "6. Open the downloaded CSV and copy your product data to our template format"
      ],
      exportLink: "https://admin.shopify.com/store/[your-store]/products"
    },
    etsy: {
      name: 'Etsy Shop Template',
      headers: ['Title', 'Description', 'Price', 'Image URL', 'Listing URL', 'Category', 'Tags'],
      example: [
        'Vintage Brass Bookends',
        'Authentic vintage brass bookends from the 1960s',
        '125.00',
        'https://i.etsystatic.com/example.jpg',
        'https://etsy.com/listing/123456',
        'Home & Living',
        'vintage, brass, bookends, mid-century'
      ],
      instructions: [
        "1. Go to Your Account → Your Shop → Tools → Download Your Data",
        "2. Select 'Listings' and choose date range",
        "3. Click 'Request my data'",
        "4. Check your email for download link (may take a few hours)",
        "5. Extract listing data and reformat to match our template",
        "Note: You'll need to manually copy image URLs from your listings"
      ],
      exportLink: "https://www.etsy.com/your/privacy/download-your-data"
    },
    amazon: {
      name: 'Amazon Affiliate Template',
      headers: ['Title', 'Description', 'Price', 'Image URL', 'Amazon URL', 'ASIN', 'Category'],
      example: [
        'Design Book Collection',
        'Curated collection of essential design books',
        '45.00',
        'https://m.media-amazon.com/example.jpg',
        'https://amazon.com/dp/B123456',
        'B123456789',
        'Books'
      ],
      instructions: [
        "1. Go to Amazon Associates → Product Linking → Link to Any Page",
        "2. Search for your products and copy the product URLs",
        "3. For ASINs: Look in the URL (amazon.com/dp/[ASIN]) or product details",
        "4. For images: Right-click product image → 'Copy image address'",
        "5. Fill in our template with your affiliate URLs",
        "Tip: Use your Amazon Associates account to ensure proper attribution"
      ],
      exportLink: "https://affiliate-program.amazon.com/home/tools/linkbuilder"
    },
    custom: {
      name: 'Custom Store Template',
      headers: ['Title', 'Description', 'Price', 'Image URL', 'Product URL', 'Category', 'Tags'],
      example: [
        'Custom Product Name',
        'Detailed product description',
        '99.00',
        'https://yourstore.com/image.jpg',
        'https://yourstore.com/product',
        'Category Name',
        'tag1, tag2, tag3'
      ],
      instructions: [
        "1. Export from your e-commerce platform (WooCommerce, Squarespace, etc.)",
        "2. Most platforms: Admin → Products → Export to CSV",
        "3. Copy your product data to match our template format",
        "4. Ensure image URLs are direct links to images (not pages)",
        "5. Product URLs should go directly to your product pages",
        "6. Use high-quality images (minimum 800px width recommended)"
      ],
      exportLink: null
    }
  };

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setError('You must be logged in to apply as a contributor');
      return;
    }

    if (!legalAccepted) {
      setError('You must accept the contributor agreement to proceed');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Submit contributor application
      await addDoc(collection(db, 'contributorApplications'), {
        userId: user.uid,
        userEmail: user.email,
        ...applicationData,
        status: 'pending',
        submittedAt: new Date(),
        reviewedBy: null,
        reviewedAt: null,
        notes: ''
      });

      // Update user document to mark as applied
      await updateDoc(doc(db, 'users', user.uid), {
        contributorApplicationStatus: 'pending',
        appliedForContributorAt: new Date()
      });

      setSuccess('Application submitted successfully! We\'ll review your application within 3-5 business days.');
      setStep(2);
    } catch (err) {
      setError('Error submitting application: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSVTemplate = (platform) => {
    const template = csvTemplates[platform];
    const csvContent = [
      template.headers,
      template.example
    ];
    
    const csv = Papa.unparse(csvContent);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `artifex-${platform}-template.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setCsvFile(file);
    
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setCsvData(results.data);
        setCsvPreview(results.data.slice(0, 5)); // Show first 5 items for preview
      },
      error: (error) => {
        setError('Error parsing CSV: ' + error.message);
      }
    });
  };

  const submitProductData = async () => {
    if (!user || !csvData.length) {
      setError('No product data to submit');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create a batch submission for all products
      const submissionRef = await addDoc(collection(db, 'contributorSubmissions'), {
        userId: user.uid,
        userEmail: user.email,
        platformType,
        productCount: csvData.length,
        status: 'pending_review',
        submittedAt: new Date(),
        reviewedBy: null,
        approvedProducts: 0,
        rejectedProducts: 0
      });

      // Submit individual products for review with compliance checking
      const productPromises = csvData.map(async (product, index) => {
        const normalizedProduct = {
          title: product.Title || product.title || '',
          description: product.Description || product.description || '',
          price: parseFloat(product.Price || product.price || '0'),
          imageUrl: product['Image URL'] || product.imageUrl || product.image || '',
          productUrl: product['Product URL'] || product.productUrl || product.url || '',
          category: product.Category || product.category || 'Uncategorized',
          tags: (product.Tags || product.tags || '').split(',').map(tag => tag.trim())
        };

        // Run compliance check
        const complianceResults = await ComplianceChecker.validateProduct(
          normalizedProduct, 
          user.uid
        );

        // Log compliance check
        await ComplianceChecker.logComplianceCheck(complianceResults);

        return addDoc(collection(db, 'contributorProducts'), {
          submissionId: submissionRef.id,
          userId: user.uid,
          userEmail: user.email,
          ...normalizedProduct,
          asin: product.ASIN || product.asin || null,
          platformType,
          status: complianceResults.autoApprovalEligible ? 'approved' : 'pending_review',
          complianceRisk: complianceResults.overallRisk,
          requiresManualReview: complianceResults.requiresManualReview,
          complianceChecks: complianceResults.checks,
          submittedAt: new Date(),
          reviewedBy: null,
          reviewedAt: null,
          rejectionReason: null,
          index
        });
      });

      await Promise.all(productPromises);

      setSuccess(`Successfully submitted ${csvData.length} products for review! We'll review your products within 3-5 business days.`);
      setStep(3);
    } catch (err) {
      setError('Error submitting products: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="contributor-onboard">
        <div className="onboard-container">
          <h1>Become an Artifex Contributor</h1>
          <p>Please log in to apply as a contributor.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contributor-onboard">
      <div className="onboard-container">
        <div className="onboard-header">
          <h1>Become an Artifex Contributor</h1>
          <p>Join our curated community of creators and showcase your products to a design-focused audience.</p>
          
          <div className="onboard-steps">
            <div className={`step ${step >= 1 ? 'active' : ''}`}>
              <span>1</span> Application
            </div>
            <div className={`step ${step >= 2 ? 'active' : ''}`}>
              <span>2</span> Product Upload
            </div>
            <div className={`step ${step >= 3 ? 'active' : ''}`}>
              <span>3</span> Review
            </div>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {step === 1 && (
          <div className="application-form">
            <h2>Contributor Application</h2>
            
            {/* Legal Agreement Section */}
            <div className="legal-section">
              <h3>Contributor Agreement</h3>
              <p>Please review and accept the Artifex Contributor Agreement to proceed with your application.</p>

              <button 
                type="button" 
                onClick={() => setShowLegalTerms(!showLegalTerms)}
                className="show-terms-btn"
              >
                {showLegalTerms ? 'Hide' : 'Show'} Full Terms
              </button>

              {showLegalTerms && (
                <div className="full-terms">
                  {Object.entries(CONTRIBUTOR_TERMS.sections).map(([key, section]) => (
                    <div key={key} className="terms-section">
                      <h4>{section.title}</h4>
                      <ul>
                        {section.terms.map((term, index) => (
                          <li key={index}>{term}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}

              <label className="legal-acceptance">
                <input
                  type="checkbox"
                  checked={legalAccepted}
                  onChange={(e) => setLegalAccepted(e.target.checked)}
                  required
                />
                I have read and agree to the Artifex Contributor Agreement
              </label>
            </div>
            
            <form onSubmit={handleApplicationSubmit}>
              <div className="form-group">
                <label>Business/Creator Name *</label>
                <input
                  type="text"
                  value={applicationData.businessName}
                  onChange={(e) => setApplicationData({...applicationData, businessName: e.target.value})}
                  required
                  placeholder="Your business or creator name"
                />
              </div>

              <div className="form-group">
                <label>Business Type *</label>
                <select
                  value={applicationData.businessType}
                  onChange={(e) => setApplicationData({...applicationData, businessType: e.target.value})}
                  required
                >
                  <option value="independent_creator">Independent Creator</option>
                  <option value="small_business">Small Business</option>
                  <option value="gallery">Gallery</option>
                  <option value="studio">Design Studio</option>
                  <option value="brand">Brand</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Website/Online Store *</label>
                <input
                  type="url"
                  value={applicationData.website}
                  onChange={(e) => setApplicationData({...applicationData, website: e.target.value})}
                  required
                  placeholder="https://yourstore.com"
                />
              </div>

              <div className="form-group">
                <label>Portfolio/Instagram</label>
                <input
                  type="url"
                  value={applicationData.portfolio}
                  onChange={(e) => setApplicationData({...applicationData, portfolio: e.target.value})}
                  placeholder="https://instagram.com/yourhandle or portfolio link"
                />
              </div>

              <div className="form-group">
                <label>About Your Work *</label>
                <textarea
                  value={applicationData.bio}
                  onChange={(e) => setApplicationData({...applicationData, bio: e.target.value})}
                  required
                  placeholder="Tell us about your aesthetic, design philosophy, and what makes your work unique..."
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>Estimated Number of Products</label>
                <select
                  value={applicationData.estimatedProducts}
                  onChange={(e) => setApplicationData({...applicationData, estimatedProducts: e.target.value})}
                >
                  <option value="">Select range</option>
                  <option value="1-10">1-10 products</option>
                  <option value="11-50">11-50 products</option>
                  <option value="51-100">51-100 products</option>
                  <option value="100+">100+ products</option>
                </select>
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        )}

        {step === 2 && (
          <div className="csv-upload-section">
            <h2>Upload Your Products</h2>
            <p>Once approved, you can upload your product catalog using our CSV templates.</p>

            <div className="platform-selection">
              <h3>Select Your Platform</h3>
              <div className="platform-options">
                {Object.entries(csvTemplates).map(([key, template]) => (
                  <div key={key} className="platform-option">
                    <input
                      type="radio"
                      id={key}
                      name="platform"
                      value={key}
                      checked={platformType === key}
                      onChange={(e) => setPlatformType(e.target.value)}
                    />
                    <label htmlFor={key}>{template.name}</label>
                    <div className="platform-actions">
                      <button
                        type="button"
                        onClick={() => downloadCSVTemplate(key)}
                        className="download-template-btn"
                      >
                        Download Template
                      </button>
                      {template.exportLink && (
                        <a
                          href={template.exportLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="export-link-btn"
                        >
                          Export Data →
                        </a>
                      )}
                    </div>
                    
                    {platformType === key && (
                      <div className="platform-instructions">
                        <h4>How to export your data:</h4>
                        <ol>
                          {template.instructions.map((instruction, index) => (
                            <li key={index}>{instruction}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="csv-upload">
              <h3>Upload Your CSV</h3>
              <input
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                className="csv-input"
              />
              
              {csvPreview.length > 0 && (
                <div className="csv-preview">
                  <h4>Preview (First 5 items):</h4>
                  <div className="preview-table">
                    <table>
                      <thead>
                        <tr>
                          {Object.keys(csvPreview[0]).map(header => (
                            <th key={header}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value, i) => (
                              <td key={i}>{value}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="upload-summary">
                    <p>Total products: <strong>{csvData.length}</strong></p>
                    <button
                      type="button"
                      onClick={submitProductData}
                      className="submit-products-btn"
                      disabled={loading}
                    >
                      {loading ? 'Submitting Products...' : `Submit ${csvData.length} Products for Review`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="submission-complete">
            <h2>Submission Complete!</h2>
            <div className="next-steps">
              <h3>What happens next:</h3>
              <ol>
                <li>We'll review your application and products within 3-5 business days</li>
                <li>Approved products will appear in the Artifex Shop</li>
                <li>You'll gain access to the Contributor Dashboard</li>
                <li>Start earning commissions on sales through Artifex</li>
              </ol>
              
              <p>We'll email you at <strong>{user.email}</strong> with updates on your submission.</p>
              
              <div className="contributor-benefits">
                <h3>As an approved contributor, you'll get:</h3>
                <ul>
                  <li>✅ Product placement in curated Artifex Shop</li>
                  <li>✅ Editorial publication opportunities (including Apple News)</li>
                  <li>✅ Premium advertising placement in Signals</li>
                  <li>✅ Analytics dashboard and sales tracking</li>
                  <li>✅ Enhanced Digital Card with contributor badge</li>
                </ul>
              </div>

              <div className="completion-actions">
                <Link 
                  to={`/profiles/${user.email?.split('@')[0] || user.uid}`} 
                  className="back-to-profile-btn"
                >
                  ← Back to My Profile
                </Link>
                <Link 
                  to="/contributor-dashboard" 
                  className="dashboard-btn"
                >
                  View Contributor Dashboard
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContributorOnboard;